/**
 * **静音节点的连线样式** —— 从 Playground.vue 抽出来。
 *
 * Blender 里静音节点的连线是红色的，表达「信号直通 / 被旁路」。
 * 每次图的静音状态变化就重刷所有边的样式。
 *
 * ⚠️ **只赋值真正变化的边** —— 否则「读 edges → 写 edges」会自激成死循环。
 * 同理这里替换数组而不是原地改 `edge.style`（实测两者都能生效，选替换是因为
 * 它把「改动如何被图引擎看见」这件事显式化了）。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 */
import { watchEffect } from 'vue'
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import type { FlowEdge as Edge, FlowEdgeStyle } from '@/graph/flowTypes'
import { themeSocketColor, themeVar } from '@/theme/runtime'
import type { SocketType } from '@/types/socket'

export interface MutedEdgeStyleCtx {
  edges: { value: Edge[] }
  nodeViews: () => { id: string; data?: { model?: { muted?: boolean } } }[]
  /** 某条边的端口类型（用来算静音后的颜色） */
  socketTypeOf: (nodeId?: string | null, handleId?: string | null) => SocketType | undefined
}

export function useMutedEdgeStyle(ctx: MutedEdgeStyleCtx) {
  const { edges, nodeViews, socketTypeOf } = ctx

/* ── 静音节点的连线样式 ─────────────────────────────────────── */

/**
 * Blender 里静音节点的连线是红色的，表达「信号直通/被旁路」。
 *
 * ⚠️ 这里原来写死 `#e05252` —— 和 `--np-danger` 是**同一个值的两份抄本**，
 * 改了令牌它不跟。现在读主题解析后的实际值（兜底就是原来那个常量）。
 */
const MUTED_EDGE_STROKE_FALLBACK = '#e05252'

function mutedEdgeStroke(): string {
  return themeVar('--np-danger', MUTED_EDGE_STROKE_FALLBACK)
}

/**
 * 每次图的静音状态变化，重刷所有边的样式。
 *
 * 只赋值真正变化的边 —— 否则「读 edges → 写 edges」会自激成死循环。
 */
watchEffect(() => {
  const mutedNodes = new Set(
    nodeViews()
      .filter((n) => n.data?.model?.muted)
      .map((n) => n.id),
  )

  /*
   * 这里替换数组而不是原地改 `edge.style`（实测两者都能生效）。
   *
   * 选替换是因为它把「改动如何被图引擎看见」这件事显式化了：
   * 图引擎的 store 持有自己解析出来的对象，只有重新解析
   * （`parseEdge` 里的 `Object.assign(existingEdge, edge)`）才会同步过去。
   *
   * 顺带记一个被实测推翻的直觉：我原以为**原地改传不过去**，
   * 但 `verify:flow` 里那条断言证明传得过去 —— 别照着错直觉改代码。
   */
  type LooseEdge = {
    id?: string
    source: string
    muted?: boolean
    data?: { socketType?: SocketType }
    style?: { stroke?: string; strokeDasharray?: string }
    [key: string]: unknown
  }

  const list = edges.value as unknown as LooseEdge[]
  let changed = false

  const next = list.map((edge) => {
    // 两种静音：节点被静音（信号直通）、或这条线自己被静音
    const muted = mutedNodes.has(edge.source) || Boolean(edge.muted)
    const stroke = muted
      ? mutedEdgeStroke()
      : themeSocketColor(edge.data?.socketType ?? 'any')
    const dash = muted ? '5 3' : 'none'

    /*
     * ⚠️ `edge.style` 在我们的结构类型里是 `object`（**形状归引擎**，
     * 它允许是 `Styles | StyleFunc`）—— 这里读的两个键是我们和引擎的
     * **约定**（见 `flowTypes.FlowEdgeStyle`），所以在这一处收窄。
     */
    const style = edge.style as FlowEdgeStyle | undefined
    if (style?.stroke === stroke && style?.strokeDasharray === dash) {
      return edge
    }
    changed = true
    return { ...edge, style: { ...(style ?? {}), stroke, strokeDasharray: dash } }
  })

  // 只写真正变过的，否则「读 edges → 写 edges」会自激
  if (changed) edges.value = next as unknown as Edge[]
})


  return { MUTED_EDGE_STROKE: MUTED_EDGE_STROKE_FALLBACK }
}
